import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ArrowLeft, Check, CheckCircle2, ChevronsUpDown, Save, XCircle } from 'lucide-react';
import { useSearchParams } from 'react-router-dom';
import { Button } from '../ui/button.tsx';
import { Label } from '../ui/label.tsx';
import type { Specification } from '../Spec/SpecDataList.tsx';
import type { ReleaseTask } from './ReleaseTasksPage.tsx';
import ReleaseTaskExport from './ReleaseTaskExport.tsx';
import {
  createReleaseCompareRowsByBatch,
  createReleaseTask,
  deleteReleaseCompareRowsByBatch,
  getExperimentResultItems,
  getExperiments,
  getReleaseCompareRowsByBatch,
  getReleaseTasks,
  getReleaseMatchRows,
  getReleaseTaskById,
  getSpecifications,
  saveReleaseParameterMappings,
  updateReleaseTask,
  type CompareRowDto,
  type ExperimentResultItemDto,
} from '../../../services/coaReleaseService.ts';

interface BatchOption {
  batchNumber: string;
  latestCreatedAt: string;
}

interface MatchOption {
  id: string;
  label: string;
  analysis: string;
  analysisVariation?: string;
  testValueRaw: string;
  unit: string;
  score: number;
  branchRank: number;
}

interface Step2Row {
  rowKey: string;
  parameter: string;
  testMethod: string;
  specMinRaw: number | string | null;
  specMaxRaw: number | string | null;
  specUnit: string;
  matchOptions: MatchOption[];
  selectedMatchId: string | null;
}

interface ComparisonResult {
  batchNumber: string;
  parameter: string;
  testMethod: string;
  uom: string;
  analysis: string;
  specMin: number | null;
  specMax: number | null;
  specMinDisplay: string;
  specMaxDisplay: string;
  specUnit: string | null;
  testValue: number | null;
  testValueRaw: string;
  units: string;
  passed: boolean;
  reason?: string;
}

interface CreateReleaseTaskProps {
  specifications: Specification[];
  onBack: () => void;
  onSave: (task: ReleaseTask, results: ComparisonResult[]) => void;
}

const toNumber = (value: unknown): number | null => {
  if (typeof value === 'number') {
    return Number.isFinite(value) ? value : null;
  }

  if (typeof value === 'string') {
    const trimmed = value.trim().replace(/,/g, '');
    if (!trimmed) return null;
    const parsed = Number(trimmed.replace(/%/g, ''));
    return Number.isFinite(parsed) ? parsed : null;
  }

  return null;
};

const stripBracketContent = (value: string): string => {
  const text = String(value || '').trim();
  if (!text) return '';
  const stripped = text
    .replace(/\[[^\]]*\]/g, ' ')
    .replace(/\([^)]*\)/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
  return stripped || text;
};

const toDisplayText = (value: unknown): string => {
  if (value === null || value === undefined) return '';
  return String(value).trim();
};

const normalizeComparableText = (value: string): string =>
  value.toLowerCase().trim().replace(/\s+/g, ' ');

const toShortText = (value: string, maxLength = 10): string => {
  const text = String(value || '');
  if (text.length <= maxLength) return text;
  return `${text.slice(0, maxLength)}...`;
};

const extractBranchRank = (...values: Array<string | undefined | null>): number => {
  for (const value of values) {
    const text = String(value || '').trim();
    if (!text) continue;

    const trailingNumber = text.match(/(?:branch|variation|var|v)?\s*[-_#:]?\s*(\d+)\s*$/i);
    if (trailingNumber) {
      const rank = Number(trailingNumber[1]);
      if (Number.isFinite(rank)) return rank;
    }
  }

  return 0;
};

const buildMatchOptionLabel = (option: Pick<MatchOption, 'analysis'>) =>
  stripBracketContent(option.analysis || '') || '(No Analysis)';

const buildMatchOptionSearchText = (option: Pick<MatchOption, 'label' | 'analysis' | 'analysisVariation' | 'testValueRaw' | 'unit'>) =>
  [option.label, option.analysis, option.analysisVariation, option.testValueRaw, option.unit]
    .filter(Boolean)
    .join(' ');

interface MatchOptionComboboxProps {
  options: MatchOption[];
  value: string | null;
  onChange: (value: string | null) => void;
  placeholder?: string;
}

function MatchOptionCombobox({ options, value, onChange, placeholder = 'Type experiment name to search' }: MatchOptionComboboxProps) {
  const rootRef = useRef<HTMLDivElement | null>(null);
  const [open, setOpen] = useState(false);
  const [searchText, setSearchText] = useState('');

  const selectedOption = useMemo(
    () => options.find((option) => option.id === value) || null,
    [options, value]
  );

  const filteredOptions = useMemo(() => {
    const normalizedSearch = normalizeComparableText(searchText);
    if (!normalizedSearch) {
      return options;
    }

    return options.filter((option) =>
      normalizeComparableText(buildMatchOptionSearchText(option)).includes(normalizedSearch)
    );
  }, [options, searchText]);

  const unmatchOptionVisible = useMemo(() => {
    const normalizedSearch = normalizeComparableText(searchText);
    return !normalizedSearch || 'unmatch'.includes(normalizedSearch);
  }, [searchText]);

  useEffect(() => {
    const handleDocumentMouseDown = (event: MouseEvent) => {
      if (!rootRef.current) return;
      if (!rootRef.current.contains(event.target as Node)) {
        setOpen(false);
        setSearchText('');
      }
    };

    document.addEventListener('mousedown', handleDocumentMouseDown);
    return () => document.removeEventListener('mousedown', handleDocumentMouseDown);
  }, []);

  return (
    <div ref={rootRef} className="relative">
      <Button
        type="button"
        variant="outline"
        role="combobox"
        aria-expanded={open}
        className="w-full justify-between font-normal"
        onClick={() => setOpen((current) => !current)}
      >
        <span className={selectedOption ? 'truncate text-left' : 'truncate text-left text-gray-500'}>
          {selectedOption ? `[${selectedOption.score}] ${selectedOption.label}` : placeholder}
        </span>
        <ChevronsUpDown className="ml-2 size-4 shrink-0 opacity-50" />
      </Button>

      {open && (
        <div className="absolute left-0 right-0 top-full z-50 mt-2 w-full rounded-md border border-gray-200 bg-white p-2 shadow-lg">
          <input
            autoFocus
            className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm outline-none focus:border-blue-500"
            placeholder="Search by experiment name..."
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
          />

          <div className="mt-2 max-h-64 overflow-auto rounded-md border border-gray-100">
            {unmatchOptionVisible && (
              <button
                type="button"
                className={`flex w-full items-center gap-2 px-3 py-2 text-left text-sm hover:bg-gray-50 ${value === null ? 'bg-blue-50' : ''}`}
                onClick={() => {
                  onChange(null);
                  setOpen(false);
                  setSearchText('');
                }}
              >
                <Check className={`mr-2 size-4 ${value === null ? 'opacity-100' : 'opacity-0'}`} />
                <span className="truncate text-left">[0] Unmatch</span>
              </button>
            )}
            {filteredOptions.length === 0 ? (
              <div className="px-3 py-4 text-sm text-gray-500">No matching experiment found.</div>
            ) : (
              filteredOptions.map((option) => {
                const isSelected = option.id === value;
                return (
                  <button
                    key={option.id}
                    type="button"
                    className={`flex w-full items-center gap-2 px-3 py-2 text-left text-sm hover:bg-gray-50 ${isSelected ? 'bg-blue-50' : ''}`}
                    onClick={() => {
                      onChange(option.id);
                      setOpen(false);
                      setSearchText('');
                    }}
                  >
                    <Check className={`mr-2 size-4 ${isSelected ? 'opacity-100' : 'opacity-0'}`} />
                    <span className="truncate text-left">{`[${option.score}] ${option.label}`}</span>
                  </button>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
}

const sortMatchOptions = (options: MatchOption[]) => [...options].sort((a, b) => {
  const scoreDiff = Number(b.score || 0) - Number(a.score || 0);
  if (scoreDiff !== 0) return scoreDiff;

  const branchDiff = Number(b.branchRank || 0) - Number(a.branchRank || 0);
  if (branchDiff !== 0) return branchDiff;

  return String(a.analysis || '').localeCompare(String(b.analysis || ''));
});

const normalizeUnitText = (value: string): string => String(value || '')
  .toLowerCase()
  .replace(/[μµ]/g, 'u')
  .replace(/\s+/g, '')
  .replace(/per/g, '/')
  .trim();

const normalizeUnitToken = (value: string): string => normalizeUnitText(value)
  .replace(/micrograms?|mcg/g, 'ug')
  .replace(/kilogram|kgs?/g, 'kg')
  .replace(/grams?/g, 'g')
  .replace(/milligrams?/g, 'mg')
  .replace(/kilojoules?/g, 'kj')
  .replace(/joules?/g, 'j')
  .replace(/percent|pct/g, '%');

const getKnownUnitBaseFactor = (unit: string): { family: string; factor: number } | null => {
  const normalized = normalizeUnitToken(unit);
  const factors: Record<string, { family: string; factor: number }> = {
    kg: { family: 'mass', factor: 1000 },
    g: { family: 'mass', factor: 1 },
    mg: { family: 'mass', factor: 0.001 },
    ug: { family: 'mass', factor: 0.000001 },
    kj: { family: 'energy', factor: 1000 },
    j: { family: 'energy', factor: 1 },
    kcal: { family: 'energy', factor: 4184 },
    cal: { family: 'energy', factor: 4.184 },
    '%': { family: 'ratio', factor: 1 },
  };

  return factors[normalized] || null;
};

const parseUnitExpression = (unit: string): { numerator: string; denominator: string } | null => {
  const normalized = normalizeUnitText(unit)
    .replace(/\([^)]*\)/g, '')
    .replace(/\[[^\]]*\]/g, '');
  if (!normalized) return null;

  const [rawNumerator, ...rawDenominatorParts] = normalized.split('/');
  const numeratorMatch = rawNumerator.match(/[a-z%]+/i);
  if (!numeratorMatch) return null;

  return {
    numerator: normalizeUnitToken(numeratorMatch[0]),
    denominator: normalizeUnitToken(rawDenominatorParts.join('/')),
  };
};

const getUnitExpressionTokens = (unit: string): string[] => {
  const normalized = normalizeUnitText(unit)
    .replace(/\([^)]*\)/g, '')
    .replace(/\[[^\]]*\]/g, '');
  const matches = normalized.match(/[a-z%]+/gi) || [];

  return matches
    .map((token) => normalizeUnitToken(token))
    .filter(Boolean);
};

const unitsShareComparableToken = (fromUnit: string, toUnit: string): boolean => {
  const fromTokens = new Set(getUnitExpressionTokens(fromUnit));
  const toTokens = getUnitExpressionTokens(toUnit);

  return toTokens.some((token) => fromTokens.has(token));
};

const getUnitConversionFactor = (fromUnit: string, toUnit: string): number | null => {
  const normalizedFrom = normalizeUnitText(fromUnit);
  const normalizedTo = normalizeUnitText(toUnit);
  if (!normalizedFrom || !normalizedTo) return null;
  if (normalizedFrom === normalizedTo) return 1;

  const from = parseUnitExpression(fromUnit);
  const to = parseUnitExpression(toUnit);
  if (!from || !to) return null;

  const denominatorCompatible =
    from.denominator === to.denominator ||
    !from.denominator ||
    !to.denominator;
  if (!denominatorCompatible) return null;

  const fromFactor = getKnownUnitBaseFactor(from.numerator);
  const toFactor = getKnownUnitBaseFactor(to.numerator);
  if (fromFactor && toFactor && fromFactor.family === toFactor.family) {
    return fromFactor.factor / toFactor.factor;
  }

  if (from.numerator === to.numerator) {
    return 1;
  }

  return null;
};

const formatConvertedNumber = (value: number): string => {
  if (!Number.isFinite(value)) return '';
  return Number(value.toFixed(6)).toString();
};

interface UnitConversionResult {
  valueRaw: string;
  unit: string;
  converted: boolean;
  aligned: boolean;
  reason: string;
}

const convertTestValueRawToUnit = (
  testValueRaw: string,
  fromUnit: string,
  toUnit: string
): UnitConversionResult => {
  const factor = getUnitConversionFactor(fromUnit, toUnit);
  if (factor === null) {
    const normalizedFrom = normalizeUnitText(fromUnit);
    const normalizedTo = normalizeUnitText(toUnit);
    const hasMismatch = !!normalizedFrom && !!normalizedTo && normalizedFrom !== normalizedTo;
    const hasSharedToken = hasMismatch && unitsShareComparableToken(fromUnit, toUnit);
    return {
      valueRaw: testValueRaw,
      unit: fromUnit || toUnit,
      converted: false,
      aligned: !hasMismatch || hasSharedToken,
      reason: hasMismatch && !hasSharedToken ? `Cannot convert unit from ${fromUnit} to ${toUnit}` : '',
    };
  }

  const normalizedFrom = normalizeUnitText(fromUnit);
  const normalizedTo = normalizeUnitText(toUnit);
  if (!testValueRaw || normalizedFrom === normalizedTo) {
    return {
      valueRaw: testValueRaw,
      unit: toUnit || fromUnit,
      converted: false,
      aligned: true,
      reason: '',
    };
  }

  const convertedParts = String(testValueRaw || '').split(',').map((part) => {
    const token = part.trim();
    if (!token) return token;

    const comparatorMatch = token.match(/^([<>]=?|≤|≥)\s*(-?\d+(?:\.\d+)?)$/);
    if (comparatorMatch) {
      return `${comparatorMatch[1]}${formatConvertedNumber(Number(comparatorMatch[2]) * factor)}`;
    }

    const numberMatch = token.match(/^-?\d+(?:\.\d+)?$/);
    if (numberMatch) {
      return formatConvertedNumber(Number(token) * factor);
    }

    return token;
  });

  return {
    valueRaw: convertedParts.join(', '),
    unit: toUnit || fromUnit,
    converted: factor !== 1,
    aligned: true,
    reason: '',
  };
};

const evaluateSingleToken = (
  token: string,
  specMin: number | null,
  specMax: number | null,
  specMinText: string,
  specMaxText: string
): { passed: boolean; reason: string } => {
  const raw = token.trim();
  if (!raw) return { passed: true, reason: '' };

  const normalizedToken = normalizeComparableText(raw);

  const comparatorMatch = raw.match(/^([<>]=?|≤|≥)\s*(-?\d+(?:\.\d+)?)$/);
  if (comparatorMatch) {
    const op = comparatorMatch[1];
    const threshold = Number(comparatorMatch[2]);
    if (!Number.isFinite(threshold)) {
      return { passed: false, reason: `${raw} is not a valid number` };
    }

    if (op === '<' || op === '<=' || op === '≤') {
      if (specMin === null && specMax !== null) {
        const passed = op === '<' ? threshold < specMax : threshold <= specMax;
        return {
          passed,
          reason: passed ? '' : `${raw} is not lower than Spec Max (${specMax})`,
        };
      }

      if (specMin === null) {
        return { passed: false, reason: `Spec Min and Spec Max are missing for ${raw}` };
      }

      const passed = op === '<' ? threshold < specMin : threshold <= specMin;
      return {
        passed,
        reason: passed ? '' : `${raw} is not lower than Spec Min (${specMin})`,
      };
    }

    if (specMax === null && specMin !== null) {
      const passed = op === '>' ? threshold > specMin : threshold >= specMin;
      return {
        passed,
        reason: passed ? '' : `${raw} is not higher than Spec Min (${specMin})`,
      };
    }

    if (specMax === null) {
      return { passed: false, reason: `Spec Max and Spec Min are missing for ${raw}` };
    }

    const passed = op === '>' ? threshold > specMax : threshold >= specMax;
    return {
      passed,
      reason: passed ? '' : `${raw} is not higher than Spec Max (${specMax})`,
    };
  }

  const plainNumber = toNumber(raw);
  if (plainNumber !== null) {
    // Business rule: testValue < 0.4 and specMax > 0.4 is considered pass.
    if (plainNumber < 0.4 && specMax !== null && specMax > 0.4) {
      return {
        passed: true,
        reason: '',
      };
    }

    const reasons: string[] = [];
    if (specMin !== null && plainNumber < specMin) {
      reasons.push(`${plainNumber} below Spec Min (${specMin})`);
    }
    if (specMax !== null && plainNumber > specMax) {
      reasons.push(`${plainNumber} above Spec Max (${specMax})`);
    }
    return {
      passed: reasons.length === 0,
      reason: reasons.join('; '),
    };
  }

  const specCandidates = [specMinText, specMaxText]
    .map((value) => normalizeComparableText(value))
    .filter(Boolean);

  if (specCandidates.length === 0) {
    return { passed: false, reason: `Spec text is missing for string value ${raw}` };
  }

  const maxSpecLength = specCandidates.reduce((max, candidate) => Math.max(max, candidate.length), 0);

  // Business rule:
  // 1) If spec string length > 10, test value containing "matching" passes.
  // 2) If spec string length <= 10, only exact match passes.
  if (maxSpecLength > 10) {
    if (normalizedToken.includes('matching')) {
      return { passed: true, reason: '' };
    }

    return {
      passed: false,
      reason: `String value ${raw} must contain 'matching' when spec text length is greater than 10`,
    };
  }

  const passed = specCandidates.includes(normalizedToken);
  return {
    passed,
    reason: passed ? '' : `String value ${raw} does not match Spec Min/Max`,
  };
};

const evaluateTestValueAgainstSpec = (
  testValueRaw: string,
  specMinRaw: number | string | null,
  specMaxRaw: number | string | null
): { passed: boolean; reason: string; numericValue: number | null } => {
  const values = String(testValueRaw || '')
    .split(',')
    .map((value) => value.trim())
    .filter(Boolean);

  if (values.length === 0) {
    return { passed: false, reason: 'TestValue is empty', numericValue: null };
  }

  const specMin = toNumber(specMinRaw);
  const specMax = toNumber(specMaxRaw);
  const specMinText = toDisplayText(specMinRaw);
  const specMaxText = toDisplayText(specMaxRaw);

  const reasons: string[] = [];
  values.forEach((token) => {
    const result = evaluateSingleToken(token, specMin, specMax, specMinText, specMaxText);
    if (!result.passed && result.reason) {
      reasons.push(result.reason);
    }
  });

  const numericValue = values.length === 1 ? toNumber(values[0]) : null;
  return {
    passed: reasons.length === 0,
    reason: reasons.join('; '),
    numericValue,
  };
};


export default function CreateReleaseTask({ specifications, onBack, onSave }: CreateReleaseTaskProps) {
  const [searchParams] = useSearchParams();
  const mode = searchParams.get('mode');
  const taskId = searchParams.get('taskId');
  const sourceTaskId = searchParams.get('sourceTaskId');
  const isViewMode = mode === 'view';
  const isRetestMode = mode === 'retest';

  const [step, setStep] = useState<1 | 2 | 3>(1);
  const [batchOptions, setBatchOptions] = useState<BatchOption[]>([]);
  const [selectedBatch, setSelectedBatch] = useState('');
  const [step2Rows, setStep2Rows] = useState<Step2Row[]>([]);
  const [comparisonResults, setComparisonResults] = useState<ComparisonResult[]>([]);
  const [loadingData, setLoadingData] = useState(false);
  const [savingTask, setSavingTask] = useState(false);
  const [errorText, setErrorText] = useState('');

  const buildComparisonRows = useCallback((rows: Step2Row[], batchNumber: string): ComparisonResult[] => {
    const matchedRows = rows.filter((row) => row.selectedMatchId);
    return matchedRows.map((row) => {
      const match = row.matchOptions.find((item) => item.id === row.selectedMatchId) || null;
      const rawTestValue = match?.testValueRaw || '';
      const alignedTestValue = convertTestValueRawToUnit(rawTestValue, match?.unit || '', row.specUnit || '');
      const testValueRaw = alignedTestValue.valueRaw;
      const evaluation = evaluateTestValueAgainstSpec(testValueRaw, row.specMinRaw, row.specMaxRaw);

      return {
        batchNumber,
        parameter: row.parameter,
        testMethod: row.testMethod,
        uom: row.specUnit || '',
        analysis: match?.analysis || '',
        specMin: toNumber(row.specMinRaw),
        specMax: toNumber(row.specMaxRaw),
        specMinDisplay: toDisplayText(row.specMinRaw),
        specMaxDisplay: toDisplayText(row.specMaxRaw),
        specUnit: row.specUnit || null,
        testValue: evaluation.numericValue,
        testValueRaw,
        units: alignedTestValue.unit || match?.unit || '',
        passed: evaluation.passed,
        reason: evaluation.reason,
      };
    });
  }, []);

  const mapCompareDtosToRows = useCallback((batchNumber: string, compareItems: CompareRowDto[]): ComparisonResult[] => {
    return compareItems.map((item) => {
      const testValueRaw = String(item.testValue || '');
      const unitAlignment = convertTestValueRawToUnit(testValueRaw, item.units || '', item.uom || '');
      const evaluation = evaluateTestValueAgainstSpec(unitAlignment.valueRaw, item.specMin, item.specMax);
      const passed = evaluation.passed;

      return {
        batchNumber: item.batchNumber || batchNumber,
        parameter: item.parameter || '-',
        testMethod: item.testMethod || '-',
        uom: item.uom || '-',
        analysis: item.analysis || '-',
        specMin: toNumber(item.specMin),
        specMax: toNumber(item.specMax),
        specMinDisplay: String(item.specMin || ''),
        specMaxDisplay: String(item.specMax || ''),
        specUnit: item.uom || null,
        testValue: evaluation.numericValue,
        testValueRaw: unitAlignment.valueRaw,
        units: unitAlignment.unit || item.units || '',
        passed,
        reason: passed ? '' : evaluation.reason,
      };
    });
  }, []);

  const fetchCompareRowsByBatch = useCallback(async (batchNumber: string): Promise<ComparisonResult[]> => {
    const compareItems = await getReleaseCompareRowsByBatch(batchNumber);

    return mapCompareDtosToRows(batchNumber, compareItems);
  }, [mapCompareDtosToRows]);

  const clearCompareRowsForBatch = useCallback(async (batchNumber: string) => {
    const existingRows = await getReleaseCompareRowsByBatch(batchNumber);
    if (existingRows.length > 0) {
      await deleteReleaseCompareRowsByBatch(
        existingRows
          .map((item) => Number(item.id))
          .filter((id) => Number.isFinite(id))
      );
    }
  }, []);

  const saveCompareRowsForBatch = useCallback(async (batchNumber: string, rows: ComparisonResult[]) => {
    await clearCompareRowsForBatch(batchNumber);

    const payloadItems = rows.map((item) => ({
      BatchNumber: batchNumber,
      Parameter: item.parameter,
      TestMethod: item.testMethod || '',
      Uom: item.uom || '',
      Analysis: item.analysis || '',
      Units: item.units || '',
      SpecMin: item.specMinDisplay || '',
      SpecMax: item.specMaxDisplay || '',
      TestValue: item.testValueRaw || String(item.testValue ?? ''),
      TestResult: item.passed ? 'passed' : 'failed',
    }));

    await createReleaseCompareRowsByBatch(payloadItems);
  }, [clearCompareRowsForBatch]);

  const applyBackendMatching = useCallback(async (batchNumber: string, rows: Step2Row[]): Promise<Step2Row[] | null> => {
    const payload = {
      batchNumber,
      rows: rows.map((row) => ({
        rowKey: row.rowKey,
        parameter: row.parameter,
        testMethod: row.testMethod,
        options: row.matchOptions.map((option) => ({
          id: option.id,
          analysis: option.analysis,
          score: option.score,
        })),
      })),
    };

    const backendRows = await getReleaseMatchRows(payload);
    if (!backendRows || backendRows.length === 0) {
      return null;
    }

    const mapped = rows.map((row) => {
      const backendRow =
        backendRows.find((item) => item.rowKey === row.rowKey) ||
        backendRows.find((item) => normalizeComparableText(item.parameter || '') === normalizeComparableText(row.parameter || ''));

      if (!backendRow) {
        return row;
      }

      const backendOptions = backendRow.options || [];

      const mergedOptions = row.matchOptions.map((option) => {
        const fromBackend = backendOptions.find((item) => item.id === option.id);
        const merged = fromBackend
          ? {
              ...option,
              analysis: fromBackend.analysis ?? option.analysis,
              unit: fromBackend.unit ?? option.unit,
              testValueRaw: fromBackend.testValueRaw ?? option.testValueRaw,
              score: typeof fromBackend.score === 'number' ? fromBackend.score : option.score,
            }
          : option;

        return {
          ...merged,
          label: buildMatchOptionLabel(merged),
        };
      });

      const sortedOptions = sortMatchOptions(mergedOptions);
      const selectedMatchId = sortedOptions[0]?.id ?? backendRow.defaultSelectedId ?? backendRow.selectedMatchId ?? row.selectedMatchId;

      return {
        ...row,
        matchOptions: sortedOptions,
        selectedMatchId,
      };
    });

    return mapped;
  }, []);

  const fetchBatchOptions = useCallback(async () => {
    setLoadingData(true);
    setErrorText('');
    try {
      const params = new URLSearchParams();
      params.set('range', '[0,9999]');
      params.set('perPage', '10000');

      const experiments = await getExperiments({
        range: params.get('range') || '[0,9999]',
        perPage: params.get('perPage') || '10000',
      });
      const map = new Map<string, string>();
      experiments.forEach((item) => {
        const batch = String(item.batchNumber || '').trim();
        if (!batch) return;
        const createdAt = item.createdAt || '';
        const current = map.get(batch);
        if (!current || new Date(createdAt).getTime() > new Date(current).getTime()) {
          map.set(batch, createdAt);
        }
      });

      const options = Array.from(map.entries())
        .map(([batchNumber, latestCreatedAt]) => ({ batchNumber, latestCreatedAt }))
        .sort((a, b) => new Date(b.latestCreatedAt).getTime() - new Date(a.latestCreatedAt).getTime());

      setBatchOptions(options);
    } catch (error) {
      setErrorText(error instanceof Error ? error.message : 'Load batches failed.');
    } finally {
      setLoadingData(false);
    }
  }, []);

  const getExistingReleaseTaskForBatch = useCallback(async (batchNumber: string) => {
    const params = new URLSearchParams();
    params.set('range', '[0,9999]');
    params.set('perPage', '10000');
    params.set('filter', JSON.stringify({ batchNumber }));

    const existing = await getReleaseTasks({
      range: params.get('range') || '[0,9999]',
      perPage: params.get('perPage') || '10000',
      filter: params.get('filter') || '',
    });

    const normalizedBatch = normalizeComparableText(batchNumber);
    return existing.find((item) => normalizeComparableText(String(item.batchNumber || '')) === normalizedBatch) || null;
  }, []);

  const loadStep2Data = useCallback(async (batchNumber: string) => {
    setLoadingData(true);
    setErrorText('');
    try {
      const normalizedBatchNumber = batchNumber.trim();
      if (!normalizedBatchNumber) {
        throw new Error('Batch Number is required.');
      }

      if (!isRetestMode) {
        const existingTask = await getExistingReleaseTaskForBatch(normalizedBatchNumber);
        if (existingTask) {
          throw new Error(`Batch ${normalizedBatchNumber} already has an existing release task.`);
        }
      } else {
        await clearCompareRowsForBatch(normalizedBatchNumber);
      }

      const specParams = new URLSearchParams();
      specParams.set('range', '[0,999]');
      specParams.set('perPage', '1000');
      specParams.set('filter', JSON.stringify({ batchNumber: normalizedBatchNumber }));

      const experimentParams = new URLSearchParams();
      experimentParams.set('range', '[0,500]');
      experimentParams.set('perPage', '500');
      experimentParams.set('filter', JSON.stringify({ batchNumber: normalizedBatchNumber }));

      const [specList, experiments] = await Promise.all([
        getSpecifications({
          range: specParams.get('range') || '[0,999]',
          perPage: specParams.get('perPage') || '1000',
          filter: specParams.get('filter') || '',
        }) as Promise<Specification[]>,
        getExperiments({
          range: experimentParams.get('range') || '[0,500]',
          perPage: experimentParams.get('perPage') || '500',
          filter: experimentParams.get('filter') || '',
        }),
      ]);

      const exactSpecs = specList.filter((item) => String(item.batchNumber || '').trim() === normalizedBatchNumber);
      if (exactSpecs.length === 0) {
        throw new Error('No specification records found for selected batch.');
      }

      const exactExperiments = experiments.filter((item) => String(item.batchNumber || '').trim() === normalizedBatchNumber);
      if (exactExperiments.length === 0) {
        throw new Error('No experiment records found for selected batch.');
      }

      const allExperimentItems = (
        await Promise.all(
          exactExperiments.map(async (exp) => {
            const params = new URLSearchParams();
            params.set('range', '[0,2000]');
            params.set('perPage', '2000');
            params.set('filter', JSON.stringify({ experimentId: exp.id }));

            try {
              const items = await getExperimentResultItems({
                range: params.get('range') || '[0,2000]',
                perPage: params.get('perPage') || '2000',
                filter: params.get('filter') || '',
              });
              return items;
            } catch {
              return [] as ExperimentResultItemDto[];
            }
          })
        )
      ).flat();

      const candidates: MatchOption[] = allExperimentItems.map((item) => {
        // Experiment name should be sampleResult + analysis
        const sample = String((item as any).sampleResult || '').trim();
        const analysisRaw = String(item.analysis || '').trim();
        const combined = `${sample} ${analysisRaw}`.trim();
        const analysis = stripBracketContent(combined);
        const analysisVariation = String(item.analysisVariation || '').trim();
        const testValueRaw = String(item.testValue || '').trim();
        const unit = String(item.unit || '').trim();
        const option = {
          id: `${item.experimentId}:${item.id}`,
          label: '',
          analysis,
          analysisVariation,
          testValueRaw,
          unit,
          score: 0,
          branchRank: extractBranchRank(analysisVariation, combined, String(item.id || '')),
        };
        return {
          ...option,
          label: buildMatchOptionLabel(option),
        };
      });

      const rows = exactSpecs.flatMap((spec, specIndex) =>
        (spec.items || []).map((item, itemIndex) => {
          return {
            rowKey: `${spec.id}-${specIndex}-${itemIndex}`,
            parameter: item.parameter || '-',
            testMethod: item.testMethod || '-',
            specMinRaw: item.specMin,
            specMaxRaw: item.specMax,
            specUnit: item.uom || '',
            matchOptions: candidates,
            selectedMatchId: null,
          } as Step2Row;
        })
      );

      const backendMatchedRows = await applyBackendMatching(normalizedBatchNumber, rows);
      if (!backendMatchedRows) {
        throw new Error('Backend matching returned no result.');
      }

      backendMatchedRows.sort((a, b) => {
        const aMatched = a.selectedMatchId ? 1 : 0;
        const bMatched = b.selectedMatchId ? 1 : 0;
        if (aMatched !== bMatched) return bMatched - aMatched;
        return a.parameter.localeCompare(b.parameter);
      });

      setStep2Rows(backendMatchedRows);
      setComparisonResults([]);
      setStep(2);
    } catch (error) {
      setErrorText(error instanceof Error ? error.message : 'Load step 2 data failed.');
      setStep2Rows([]);
      setComparisonResults([]);
    } finally {
      setLoadingData(false);
    }
  }, [applyBackendMatching, clearCompareRowsForBatch, getExistingReleaseTaskForBatch, isRetestMode]);

  useEffect(() => {
    if (isViewMode) {
      return;
    }

    void fetchBatchOptions();
  }, [fetchBatchOptions, isViewMode]);

  useEffect(() => {
    const batchFromQuery = searchParams.get('batchNumber') || '';
    if (batchFromQuery) {
      setSelectedBatch(batchFromQuery);
    }
  }, [searchParams]);

  useEffect(() => {
    if (!isViewMode) return;

    const loadViewData = async () => {
      setLoadingData(true);
      setErrorText('');
      try {
        let batchNumber = searchParams.get('batchNumber') || '';
        if (!batchNumber && taskId) {
          const task = await getReleaseTaskById(taskId);
          batchNumber = String(task?.batchNumber || '');
        }
        if (!batchNumber) {
          throw new Error('Batch number not found in view mode.');
        }

        setSelectedBatch(batchNumber);
        const rows = await fetchCompareRowsByBatch(batchNumber);

        setComparisonResults(rows);
        setStep(3);
      } catch (error) {
        setErrorText(error instanceof Error ? error.message : 'Load view mode data failed.');
      } finally {
        setLoadingData(false);
      }
    };

    void loadViewData();
  }, [fetchCompareRowsByBatch, isViewMode, searchParams, taskId]);

  const matchedCount = useMemo(
    () => step2Rows.filter((row) => row.selectedMatchId !== null).length,
    [step2Rows]
  );

  const passCount = useMemo(
    () => comparisonResults.filter((item) => item.passed).length,
    [comparisonResults]
  );

  const handleApplyStep2 = async () => {
    const batchNumber = selectedBatch.trim();
    if (!isRetestMode && !batchNumber) {
      setErrorText('Batch Number is required.');
      return;
    }

    const calculatedRows = buildComparisonRows(step2Rows, batchNumber);
    if (calculatedRows.length === 0) {
      setErrorText('No comparison rows to save.');
      return;
    }

    setLoadingData(true);
    setErrorText('');
    try {
      const selectedMappings = step2Rows
        .filter((row) => row.selectedMatchId)
        .map((row) => {
          const selectedOption = row.matchOptions.find((option) => option.id === row.selectedMatchId) || null;
          return {
            rowKey: row.rowKey,
            parameter: row.parameter,
            analysis: selectedOption?.analysis || '',
            selectedMatchId: row.selectedMatchId as string,
            testMethod: row.testMethod,
            uom: row.specUnit,
            unit: selectedOption?.unit || '',
          };
        });

      if (selectedMappings.length === 0) {
        throw new Error('No matched analysis selected.');
      }

      await saveReleaseParameterMappings({
        batchNumber,
        mappings: selectedMappings,
      });

      await saveCompareRowsForBatch(batchNumber, calculatedRows);
      const savedRows = await fetchCompareRowsByBatch(batchNumber);
      setComparisonResults(savedRows);
      setStep(3);
    } catch (error) {
      setErrorText(error instanceof Error ? error.message : 'Save step 2 matching failed.');
    } finally {
      setLoadingData(false);
    }
  };

  const handleSave = () => {
    const saveTask = async () => {
      const batchNumber = selectedBatch.trim();
      if (!isRetestMode && !batchNumber) {
        throw new Error('Batch Number is required.');
      }
      if (comparisonResults.length === 0) {
        throw new Error('No comparison rows to save.');
      }

      const allPassed = comparisonResults.every((item) => item.passed);

      const payload = {
        BatchNumber: batchNumber,
        Result: allPassed ? 'passed' : 'failed',
      };

      if (!isRetestMode) {
        const existingTask = await getExistingReleaseTaskForBatch(batchNumber);
        if (existingTask) {
          throw new Error(`Batch ${batchNumber} already has an existing release task.`);
        }
      }

      await saveCompareRowsForBatch(batchNumber, comparisonResults);
      const savedCompareRows = await fetchCompareRowsByBatch(batchNumber);

      let saved: any;
      if (isRetestMode && sourceTaskId) {
        saved = await updateReleaseTask(sourceTaskId, {
          BatchNumber: batchNumber,
          Result: payload.Result,
        });
      } else {
        saved = await createReleaseTask(payload);
      }

      const taskSource = saved?.task ?? saved;

      const newTask: ReleaseTask = {
        id: String(taskSource?.id ?? sourceTaskId ?? Date.now().toString()),
        batchNumber,
        status: allPassed ? 'passed' : 'failed',
        result: allPassed ? 'passed' : 'failed',
        createdBy: String(localStorage.getItem('username') || '-'),
        createdAt: taskSource?.createdAt || new Date().toISOString(),
        updatedAt: taskSource?.updatedAt || new Date().toISOString(),
        totalItems: savedCompareRows.length,
        passedItems: savedCompareRows.filter((item) => item.passed).length,
      };

      setComparisonResults(savedCompareRows);
      onSave(newTask, savedCompareRows);
    };

    setSavingTask(true);
    saveTask()
      .catch((error) => {
        setErrorText(error instanceof Error ? error.message : 'Save failed.');
      })
      .finally(() => setSavingTask(false));
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-4">
        <Button variant="outline" onClick={onBack} className="hover:bg-gray-50">
          <ArrowLeft className="size-4 mr-2" />
          Back
        </Button>
        <div>
          <h2 className="text-2xl">Create Release Task</h2>
          <p className="text-sm text-gray-500">3 Steps: Release batch -&gt; Confirmate Specifications -&gt; Release result</p>
        </div>
      </div>

      {!isViewMode && (
        <div className="flex items-center justify-center gap-4 py-4">
          <div className="flex items-center gap-2">
            <div className={`h-10 w-10 rounded-full flex items-center justify-center ${step === 1 ? 'bg-blue-500 text-white' : 'bg-green-500 text-white'}`}>1</div>
            <span className={step === 1 ? 'text-blue-600 text-sm' : 'text-gray-600 text-sm'}>Release batch</span>
          </div>
          <div className={`h-1 w-14 rounded ${step > 1 ? 'bg-blue-500' : 'bg-gray-300'}`} />
          <div className="flex items-center gap-2">
            <div className={`h-10 w-10 rounded-full flex items-center justify-center ${step === 2 ? 'bg-blue-500 text-white' : step > 2 ? 'bg-green-500 text-white' : 'bg-gray-300 text-gray-700'}`}>2</div>
            <span className={step === 2 ? 'text-blue-600 text-sm' : 'text-gray-600 text-sm'}>Confirmate Specifications</span>
          </div>
          <div className={`h-1 w-14 rounded ${step > 2 ? 'bg-blue-500' : 'bg-gray-300'}`} />
          <div className="flex items-center gap-2">
            <div className={`h-10 w-10 rounded-full flex items-center justify-center ${step === 3 ? 'bg-blue-500 text-white' : 'bg-gray-300 text-gray-700'}`}>3</div>
            <span className={step === 3 ? 'text-blue-600 text-sm' : 'text-gray-600 text-sm'}>Release result</span>
          </div>
        </div>
      )}

      {errorText && (
        <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700">
          {errorText}
        </div>
      )}

      {!isViewMode && step === 1 && (
        <div className="rounded-xl border border-gray-200 bg-white p-6 shadow-sm space-y-4">
          <div className="flex items-center justify-between border-b pb-3">
            <div>
              <h3 className="text-lg">Step 1: Release batch</h3>
              <span className="text-xs text-gray-500">Distinct from Experiment.BatchNumber (latest first)</span>
            </div>
            <div className="flex items-center gap-2">
              <Button
                type="button"
                disabled={!selectedBatch || loadingData}
                onClick={() => {
                  const value = selectedBatch.trim();
                  if (!isRetestMode) {
                    const exact = batchOptions.some((item) => item.batchNumber === value);
                    if (!exact) {
                      setErrorText('Please select an existing batch number from the dropdown list.');
                      return;
                    }
                  }
                  void loadStep2Data(value);
                }}
              >
                Next
              </Button>
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="batchSelectInput">Release batch</Label>
            <input
              id="batchSelectInput"
              list="release-batch-options"
              className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
              value={selectedBatch}
              onChange={(e) => setSelectedBatch(e.target.value)}
              placeholder="Type batch number to search, then select from dropdown"
              disabled={loadingData}
              readOnly={isRetestMode && !!searchParams.get('batchNumber')}
            />
            <datalist id="release-batch-options">
              {batchOptions.map((item) => (
                <option key={item.batchNumber} value={item.batchNumber}>
                  {item.batchNumber} ({new Date(item.latestCreatedAt).toLocaleString('zh-CN', { hour12: false })})
                </option>
              ))}
            </datalist>
            <p className="text-xs text-gray-500">Input to filter, then select one batch from the dropdown list.</p>
          </div>

        </div>
      )}

      {!isViewMode && step === 2 && (
        <div className="rounded-xl border border-gray-200 bg-white p-6 shadow-sm space-y-4">
          <div className="flex items-center justify-between border-b pb-3">
            <div>
              <h3 className="text-lg">Step 2: Confirmate Specifications</h3>
              <p className="text-sm text-gray-500">batch number:{selectedBatch}</p>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-sm text-gray-600 mr-2">Matched {matchedCount} / {step2Rows.length}</span>
              <Button variant="outline" onClick={() => setStep(1)}>
                Back
              </Button>
              <Button
                type="button"
                disabled={matchedCount === 0}
                onClick={() => { void handleApplyStep2(); }}
              >
                Next
              </Button>
            </div>
          </div>

          <div className="overflow-x-auto rounded-lg border border-gray-200">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-gray-50 border-b border-gray-200">
                  <th className="px-3 py-2 text-left">Spec Name</th>
                  <th className="px-3 py-2 text-left">Test Method</th>
                  <th className="px-3 py-2 text-left">UOM</th>
                  <th className="px-3 py-2 text-left">Experiment Name</th>
                  <th className="px-3 py-2 text-left">Unit</th>
                </tr>
              </thead>
              <tbody>
                {step2Rows.map((row) => {
                  const selected = row.matchOptions.find((item) => item.id === row.selectedMatchId) || null;
                  const hasMatch = !!row.selectedMatchId;
                  const availableOptions: MatchOption[] = sortMatchOptions(row.matchOptions);
                  return (
                    <tr key={row.rowKey} className={hasMatch ? 'bg-green-50 border-b border-gray-100' : 'bg-white border-b border-gray-100'}>
                      <td className="px-3 py-2">{row.parameter}</td>
                      <td className="px-3 py-2">{row.testMethod || '-'}</td>
                      <td className="px-3 py-2">{row.specUnit || '-'}</td>
                      <td className="px-3 py-2 min-w-[360px]">
                        <MatchOptionCombobox
                          options={availableOptions}
                          value={row.selectedMatchId}
                          onChange={(value) => {
                            setStep2Rows((prev) => prev.map((item) => (
                              item.rowKey === row.rowKey
                                ? { ...item, selectedMatchId: value }
                                : item
                            )));
                          }}
                        />
                      </td>
                      <td className="px-3 py-2">{selected?.unit || '-'}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {step === 3 && (
        <div className="rounded-xl border border-gray-200 bg-white p-6 shadow-sm space-y-4">
          <div className="flex items-center justify-between border-b pb-3">
            <div>
              <h3 className="text-lg">Step 3: Release result</h3>
              <p className="text-sm text-gray-500">batch number:{selectedBatch || '-'}</p>
            </div>
            <div className="flex items-center gap-2">
              <ReleaseTaskExport
                batchNumber={selectedBatch}
                comparisonResults={comparisonResults}
                disabled={savingTask || comparisonResults.length === 0}
              />
              {!isViewMode && (
                <>
                  <Button variant="outline" onClick={() => setStep(2)}>
                    Back
                  </Button>
                  <Button type="button" disabled={savingTask} onClick={handleSave}>
                    <Save className="size-4 mr-2" />
                    {savingTask ? 'Saving...' : 'Save Release Task'}
                  </Button>
                </>
              )}
              {comparisonResults.every((item) => item.passed) ? (
                <>
                  <CheckCircle2 className="size-5 text-green-600" />
                  <span className="text-green-600 text-sm">All Passed</span>
                </>
              ) : (
                <>
                  <XCircle className="size-5 text-red-600" />
                  <span className="text-red-600 text-sm">Has Failed Items</span>
                </>
              )}
            </div>
          </div>

          <div className="grid grid-cols-3 gap-3">
            <div className="rounded border border-gray-200 bg-gray-50 px-3 py-2 text-center">
              <div className="text-xl">{comparisonResults.length}</div>
              <div className="text-xs text-gray-600">Total</div>
            </div>
            <div className="rounded border border-green-200 bg-green-50 px-3 py-2 text-center">
              <div className="text-xl text-green-700">{passCount}</div>
              <div className="text-xs text-green-700">Pass</div>
            </div>
            <div className="rounded border border-red-200 bg-red-50 px-3 py-2 text-center">
              <div className="text-xl text-red-700">{comparisonResults.length - passCount}</div>
              <div className="text-xs text-red-700">Failed</div>
            </div>
          </div>

          <div className="overflow-hidden rounded-lg border border-gray-200">
            <table className="w-full table-fixed text-xs md:text-sm">
              <thead>
                <tr className="bg-gray-50 border-b border-gray-200">
                  <th className="px-3 py-2 text-left whitespace-normal break-words">Parameter</th>
                  <th className="px-3 py-2 text-left whitespace-normal break-words">Test Method</th>
                  <th className="px-3 py-2 text-left whitespace-normal break-words">UOM</th>
                  <th className="px-3 py-2 text-left whitespace-normal break-words">Analysis</th>
                  <th className="px-3 py-2 text-left whitespace-normal break-words">Unit</th>
                  <th className="px-3 py-2 text-left whitespace-normal break-words">Spec Min</th>
                  <th className="px-3 py-2 text-left whitespace-normal break-words">Spec Max</th>
                  <th className="px-3 py-2 text-left whitespace-normal break-words">TestValue</th>
                  <th className="px-3 py-2 text-left whitespace-normal break-words">TestResult</th>
                </tr>
              </thead>
              <tbody>
                {comparisonResults.map((item, index) => {
                  return (
                    <tr key={`${item.parameter}-${index}`} className={item.passed ? 'bg-green-50 border-b border-gray-100' : 'bg-red-50 border-b border-gray-100'}>
                      <td className="px-3 py-2 align-top whitespace-normal break-words">{item.parameter}</td>
                      <td className="px-3 py-2 align-top whitespace-normal break-words">{item.testMethod || '-'}</td>
                      <td className="px-3 py-2 align-top whitespace-normal break-words">{item.uom || '-'}</td>
                      <td className="px-3 py-2 align-top whitespace-normal break-words">{item.analysis || '-'}</td>
                      <td className="px-3 py-2 align-top whitespace-normal break-words">{item.units || '-'}</td>
                      <td
                        className="px-3 py-2 align-top whitespace-normal break-words"
                        title={item.specMinDisplay || '-'}
                      >
                        {item.specMinDisplay ? toShortText(item.specMinDisplay, 30) : '-'}
                      </td>
                      <td
                        className="px-3 py-2 align-top whitespace-normal break-words"
                        title={item.specMaxDisplay || '-'}
                      >
                        {item.specMaxDisplay ? toShortText(item.specMaxDisplay, 30) : '-'}
                      </td>
                      <td className="px-3 py-2 align-top whitespace-normal break-words">{item.testValueRaw || '-'}</td>
                      <td className="px-3 py-2 align-top whitespace-normal break-words">
                        {item.passed ? (
                          <span className="inline-flex items-center gap-1 text-green-700">
                            <CheckCircle2 className="size-4" />
                            Pass
                          </span>
                        ) : (
                          <div className="text-red-700">
                            <span className="inline-flex items-center gap-1">
                              <XCircle className="size-4" />
                              Failed
                            </span>
                            {item.reason && <div className="text-xs mt-0.5">({item.reason})</div>}
                          </div>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

        </div>
      )}

      {loadingData && (
        <div className="fixed inset-0 bg-black/20 flex items-center justify-center z-50">
          <div className="rounded-lg border border-gray-200 bg-white px-6 py-4 text-sm text-gray-700 shadow-lg">
            Loading...
          </div>
        </div>
      )}
    </div>
  );
}
